/** Dark or light, remembered per browser.
 *
 *  BOTH PALETTES ALREADY EXISTED. `src/theme.css` carries the dark and light
 *  token sets verbatim from the approved mockup, and the mockup itself has a
 *  Dark/Light control — so this is not a new design decision, it is the switch
 *  the design always assumed and the app never wired up. `index.html` had
 *  `data-theme="dark"` hard-coded and nothing ever changed it.
 *
 *  DARK REMAINS THE DEFAULT, DELIBERATELY.
 *
 *  This screen is read at 3am in a dark room, and that is the case it was
 *  designed for. The operating system's `prefers-color-scheme` is NOT consulted
 *  on a first visit: a dispatcher whose laptop is set to light would otherwise
 *  find the screen had changed under them one morning with no explanation, and
 *  a dispatcher screen that looks different from yesterday is a screen somebody
 *  has to re-learn mid-shift. The choice is theirs, once, and then it sticks.
 *
 *  WHY IT IS APPLIED BEFORE REACT MOUNTS
 *
 *  A theme applied after the first paint is a white flash on a dark screen, at
 *  night, in the dark. `apply()` is called from `main.tsx` before render, and
 *  the value it reads is on `document.body` where the CSS is already looking.
 */

export type Theme = 'dark' | 'light'

const KEY = 'adv-crm.theme'

export function current(): Theme {
  return document.body.dataset.theme === 'light' ? 'light' : 'dark'
}

/** Read the remembered choice and put it on the body. Safe to call twice. */
export function apply(): Theme {
  let stored: string | null = null
  try {
    stored = localStorage.getItem(KEY)
  } catch {
    // Private windows and blocked site data both throw on access rather than
    // returning null. A dispatcher who cannot store a preference should still
    // get a working screen, so this falls through to the default.
  }
  const theme: Theme = stored === 'light' ? 'light' : 'dark'
  document.body.dataset.theme = theme
  return theme
}

export function set(theme: Theme): void {
  document.body.dataset.theme = theme
  try {
    localStorage.setItem(KEY, theme)
  } catch {
    // The theme still applies for this session; it just will not be
    // remembered. Failing to persist must never mean failing to switch.
  }
}
